import { BehaviorSubject, Observable } from 'rxjs';
import { logger } from 'utils/logger';
import { createTheme, PaletteMode, Theme } from '@mui/material';

// Create a service-specific logger
const log = logger.getLogger('ThemeService');

// Constants
const STORAGE_KEY = 'theme';
const DEFAULT_THEME: PaletteMode = 'light';

export type ThemeType = PaletteMode;

/**
 * Color values for theme modes
 */
const themeColors = {
  light: {
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#757575',
    },
    appBar: {
      background: '#ffffff',
      color: '#333333',
    }
  },
  dark: {
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    appBar: {
      background: '#1e1e1e',
      color: '#ffffff',
    }
  },
  common: {
    primary: '#0099cc',
    secondary: '#f50057',
    fontFamily: "'Lato', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
  }
};

/**
 * Create a Material-UI theme based on the current mode
 */
export const createAppTheme = (mode: PaletteMode): Theme => {
  const colors = mode === 'light' ? themeColors.light : themeColors.dark;
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: themeColors.common.primary,
      },
      secondary: {
        main: themeColors.common.secondary,
      },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
    },
    typography: {
      fontFamily: themeColors.common.fontFamily,
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.appBar.background,
            color: colors.appBar.color,
          },
        },
      },
    },
  });
};

/**
 * Get the initial theme from localStorage or use default
 */
const getInitialTheme = (): PaletteMode => {
  try {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    return (savedTheme === 'light' || savedTheme === 'dark') 
      ? savedTheme as PaletteMode
      : DEFAULT_THEME;
  } catch (error) {
    log.error('Error accessing localStorage for theme:', error);
    return DEFAULT_THEME;
  }
};

class ThemeService {
  private themeSubject = new BehaviorSubject<ThemeType>(getInitialTheme());
  
  /**
   * Get the current theme as an Observable
   */
  getTheme$(): Observable<ThemeType> {
    return this.themeSubject.asObservable();
  }
  
  /**
   * Get the current theme value
   */
  getTheme(): ThemeType {
    return this.themeSubject.value;
  }
  
  /**
   * Set the current theme
   */
  setTheme(theme: ThemeType): void {
    localStorage.setItem(STORAGE_KEY, theme);
    this.themeSubject.next(theme);
    log.log(`Theme changed to: ${theme}`);
  }
  
  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const newTheme = this.themeSubject.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }
  
  /**
   * Get a Material-UI theme based on the current theme mode
   */
  getAppTheme(): Theme {
    return createAppTheme(this.themeSubject.value);
  }
}

const themeService = new ThemeService();
export default themeService;